import { parseIsoDateParts, toIsoDate } from './birthDateIso';

export type CalendarYmd = { y: number; m: number; d: number };

export type IsoDateRange = {
  startIso: string;
  endIso: string;
  dayCount: number;
};

export function addCalendarDays(ymd: CalendarYmd, deltaDays: number): CalendarYmd {
  const dt = new Date(ymd.y, ymd.m - 1, ymd.d);
  dt.setDate(dt.getDate() + deltaDays);
  return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
}

/** Inclusive day count between two calendar dates (same day ⇒ 1). */
export function inclusiveDayCount(start: CalendarYmd, end: CalendarYmd): number {
  const a = Date.UTC(start.y, start.m - 1, start.d);
  const b = Date.UTC(end.y, end.m - 1, end.d);
  return Math.floor((b - a) / 86_400_000) + 1;
}

/** End date from start + inclusive day count (days=1 ⇒ same day). */
export function endDateFromStartAndDays(start: CalendarYmd, days: number): CalendarYmd {
  const n = Math.max(1, Math.floor(days));
  return addCalendarDays(start, n - 1);
}

export function eachInclusiveIsoDates(startIso: string, endIso: string): string[] {
  const start = parseIsoDateParts(startIso);
  const end = parseIsoDateParts(endIso);
  if (!start || !end) {
    return [];
  }
  const count = inclusiveDayCount(start, end);
  if (count < 1) {
    return [];
  }
  const out: string[] = [];
  for (let i = 0; i < count; i += 1) {
    const ymd = addCalendarDays(start, i);
    out.push(toIsoDate(ymd.y, ymd.m, ymd.d));
  }
  return out;
}

/** Merge sorted ISO blackout keys into contiguous inclusive ranges. */
export function groupConsecutiveIsoDatesIntoRanges(isoDates: readonly string[]): IsoDateRange[] {
  const sorted = [...isoDates]
    .filter((iso) => parseIsoDateParts(iso))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  if (sorted.length === 0) {
    return [];
  }

  const ranges: IsoDateRange[] = [];
  let rangeStart = sorted[0]!;
  let rangeEnd = sorted[0]!;

  for (let i = 1; i < sorted.length; i += 1) {
    const prev = parseIsoDateParts(rangeEnd);
    const cur = sorted[i]!;
    if (!prev) {
      rangeStart = cur;
      rangeEnd = cur;
      continue;
    }
    const nextDay = addCalendarDays(prev, 1);
    const nextIso = toIsoDate(nextDay.y, nextDay.m, nextDay.d);
    if (cur === nextIso) {
      rangeEnd = cur;
      continue;
    }
    const start = parseIsoDateParts(rangeStart)!;
    const end = parseIsoDateParts(rangeEnd)!;
    ranges.push({
      startIso: rangeStart,
      endIso: rangeEnd,
      dayCount: inclusiveDayCount(start, end),
    });
    rangeStart = cur;
    rangeEnd = cur;
  }

  const start = parseIsoDateParts(rangeStart)!;
  const end = parseIsoDateParts(rangeEnd)!;
  ranges.push({
    startIso: rangeStart,
    endIso: rangeEnd,
    dayCount: inclusiveDayCount(start, end),
  });
  return ranges;
}

/** Remove every ISO key in an inclusive blackout range from `opening_times`. */
export function removeBlackoutRangeFromOpeningTimes(
  openingTimes: Record<string, unknown>,
  startIso: string,
  endIso: string,
): Record<string, unknown> {
  const next = { ...openingTimes };
  for (const iso of eachInclusiveIsoDates(startIso, endIso)) {
    delete next[iso];
  }
  return next;
}

/** Replace one stored blackout range with another (edit flow). */
export function replaceBlackoutRangeInOpeningTimes(
  openingTimes: Record<string, unknown>,
  oldStartIso: string,
  oldEndIso: string,
  newStartIso: string,
  newEndIso: string,
): Record<string, unknown> {
  let next = removeBlackoutRangeFromOpeningTimes(openingTimes, oldStartIso, oldEndIso);
  for (const iso of eachInclusiveIsoDates(newStartIso, newEndIso)) {
    next[iso] = 'closed';
  }
  return next;
}
